import { DynamicComponent } from '@gik/core/components/DynamicComponent';
import Link from '@gik/core/components/Link.tsx';
import { withTitle } from '@gik/core/hocs/withTitle';
import type WordpressPost from '@gik/core/models/wordpress/WordpressPost';
import type { WordpressTaxonomy } from '@gik/core/models/wordpress/WordpressTaxonomy';
import { ErrorPageSection } from '@gik/core/pages/Error/ErrorPageSection';
import routes from '@gik/core/routes';
import bemBlock from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { CreateClickedLocation } from '@gik/create/enums/CreateAnalyticsEnums';
import { ByGiftTypeSection } from '@gik/shop/components/ByGiftTypeSection';
import { GivePageLayout } from '@gik/shop/components/GivePageLayout';
import { GivePageHelpfulArticles } from '@gik/shop/components/RelatedArticles/GivePageHelpfulArticles';
import type { GiveTaxonomyType } from '@gik/shop/types/GiveTaxonomyType';
import { Button } from '@gik/ui-legacy/Button';
import { PageSection } from '@gik/ui-legacy/gik/PageSection';
import { StartInkindCard } from '@gik/ui-legacy/gik/StartInkindCard';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Scroll from 'react-scroll';
import { usePrevious } from 'react-use';
import { ByRecipientSection } from '../../components/ByRecipientSection';
import { BySituationSection } from '../../components/BySituationSection';
import translationKeys from '../../i18n/en';

const scroller = Scroll.scroller;

export type GivePageServerProps = {
  situations: WordpressTaxonomy[];
  recipients: WordpressTaxonomy[];
  categories: WordpressTaxonomy[];
  posts: WordpressPost[];
};

export type GivePageProps = {
  taxonomyType?: GiveTaxonomyType;
  parentTaxonomy?: string;
  fallbackData?: GivePageServerProps;
};

function GivePageComp({ taxonomyType, parentTaxonomy, fallbackData }: GivePageProps): React.ReactElement {
  const bem = bemBlock('give-page');
  const { t } = useTranslation();
  const prevTaxonomyType = usePrevious(taxonomyType);

  React.useEffect(() => {
    let delay = 0;
    // if there is no previous taxonomy type it means the user arrived here directly or they came from another page
    // in that case we need to wait a bit before attempting to scroll down
    if (!prevTaxonomyType) {
      delay = 1000;
    }

    setTimeout(() => {
      const appHeader = document.getElementsByClassName('gik-app-header')[0] as HTMLDivElement;
      scroller.scrollTo(taxonomyType, {
        duration: 500,
        smooth: true,
        // containerId: 'ContainerElementID',
        offset: -appHeader?.offsetHeight,
      });
    }, delay);
  }, [taxonomyType, prevTaxonomyType]);

  return withTitle(
    'Give',
    <GivePageLayout className={bem()}>
      <PageSection className={bem('lead')} noPadBottom>
        <p className="gik-section-lead">{t(translationKeys.leadDescription).toString()}</p>
      </PageSection>
      <DynamicComponent height={786}>
        <Scroll.Element name="situation">
          <PageSection noPad>
            <BySituationSection
              loadMoreButton
              fallbackData={fallbackData?.situations}
              taxonomyType={taxonomyType}
              parentTaxonomy={parentTaxonomy}
            />
          </PageSection>
        </Scroll.Element>
      </DynamicComponent>
      <DynamicComponent height={175}>
        <StartInkindCard trackingId={CreateClickedLocation.PAGE_FEATURES} />
      </DynamicComponent>
      <DynamicComponent height={549}>
        <Scroll.Element name="recipient">
          <PageSection noPad>
            <ByRecipientSection fallbackData={fallbackData?.recipients} />
          </PageSection>
        </Scroll.Element>
      </DynamicComponent>
      <DynamicComponent height={1346}>
        <Scroll.Element name="category">
          <PageSection noPad>
            <ByGiftTypeSection loadMoreButton fallbackData={fallbackData?.categories} />
          </PageSection>
        </Scroll.Element>
      </DynamicComponent>

      <PageSection noPadTop>
        <GivePageHelpfulArticles fallbackData={fallbackData?.posts} />
      </PageSection>
      <PageSection centered>
        <Link
          href={
            taxonomyType && parentTaxonomy
              ? `${routes.blog}/general-results/${taxonomyType}/${parentTaxonomy}`
              : routes.blog
          }
        >
          <Button variant="primary-plain">Find more helpful articles</Button>
        </Link>
      </PageSection>
    </GivePageLayout>
  );
}

export const GivePage = withComponentErrorBoundary(GivePageComp, {
  ErrorFallback: ErrorPageSection,
});
